import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  doc,
  onSnapshot,
  query,
  Timestamp,
  updateDoc,
  where,
} from "firebase/firestore";
import { db } from "../firebaseConfig";
import { useAuth } from "../../../context/AuthContext";

const labelForTarget = (item) => {
  if ((item.targetType || "vehicle") === "site") return item.siteName || item.targetLabel || "-";
  if (item.targetType === "facility") return item.facilityName || item.tesis || item.targetLabel || "-";
  return item.plate || item.targetLabel || "-";
};

const targetTypeText = (type) => {
  if (type === "site") return "Şantiye";
  if (type === "facility") return "Tesis";
  return "Araç";
};

const dateText = (value) => {
  const date = value?.toDate ? value.toDate() : value?.seconds ? new Date(value.seconds * 1000) : null;
  return date ? date.toLocaleDateString("tr-TR") : "-";
};

const latestUserForIssue = (item) => {
  if (item.lastSeenBy) return item.lastSeenBy;
  const seenHistory = Array.isArray(item.seenHistory) ? item.seenHistory : [];
  for (let i = seenHistory.length - 1; i >= 0; i -= 1) {
    if (seenHistory[i]?.createdBy) return seenHistory[i].createdBy;
  }
  return item.createdBy || "";
};

const canResolveItem = ({ item, user, canResolveAll, canResolveOwn }) => {
  if (canResolveAll) return true;
  if (!canResolveOwn) return false;
  if (!user?.email) return false;
  return item.createdBy === user.email;
};

export default function IssueBoard({ canResolveAll = false, canResolveOwn = false }) {
  const { user } = useAuth();
  const [tab, setTab] = useState("open");
  const [issues, setIssues] = useState([]);
  const [targetType, setTargetType] = useState("all");
  const [formName, setFormName] = useState("all");
  const [search, setSearch] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  useEffect(() => {
    const unsub = onSnapshot(
      query(collection(db, "kontrol_eksiklikleri"), where("resolved", "==", tab === "resolved")),
      (snap) => {
        setIssues(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      }
    );
    return () => unsub();
  }, [tab]);

  const formOptions = useMemo(() => {
    return Array.from(new Set(issues.map((item) => item.formName).filter(Boolean)))
      .sort((a, b) => a.localeCompare(b, "tr"));
  }, [issues]);

  const filtered = useMemo(() => {
    const needle = search.trim().toLocaleLowerCase("tr-TR");
    const fromKey = dateFrom || "";
    const toKey = dateTo || "";

    return issues
      .filter((item) => targetType === "all" || (item.targetType || "vehicle") === targetType)
      .filter((item) => formName === "all" || item.formName === formName)
      .filter((item) => {
        const key = tab === "resolved" ? item.resolvedDateKey || item.lastSeenDateKey : item.lastSeenDateKey || item.firstSeenDateKey;
        if (fromKey && key < fromKey) return false;
        if (toKey && key > toKey) return false;
        return true;
      })
      .filter((item) => {
        if (!needle) return true;
        return [
          labelForTarget(item),
          item.issue,
          item.lastNote,
          item.createdBy,
          latestUserForIssue(item),
          item.resolvedBy,
          item.formName,
        ].some((value) => String(value || "").toLocaleLowerCase("tr-TR").includes(needle));
      })
      .sort((a, b) => (b.lastSeenAt?.seconds || b.firstSeenAt?.seconds || 0) - (a.lastSeenAt?.seconds || a.firstSeenAt?.seconds || 0));
  }, [issues, targetType, formName, search, dateFrom, dateTo, tab]);

  const resolveIssue = async (item) => {
    if (!canResolveItem({ item, user, canResolveAll, canResolveOwn })) {
      return alert("Bu işlem için yetkiniz yok.");
    }
    await updateDoc(doc(db, "kontrol_eksiklikleri", item.id), {
      resolved: true,
      resolvedAt: Timestamp.now(),
      resolvedDateKey: new Date().toISOString().slice(0, 10),
      resolvedBy: user?.email || "",
    });
  };

  return (
    <div style={pageStyle}>
      <section style={panelStyle}>
        <h2 style={titleStyle}>Kontrol Eksiklikleri</h2>
        <div style={toolbarStyle}>
          <button type="button" onClick={() => setTab("open")} style={tab === "open" ? activeTabBtn : tabBtn}>Açık</button>
          <button type="button" onClick={() => setTab("resolved")} style={tab === "resolved" ? activeTabBtn : tabBtn}>Çözülen</button>
          <select value={targetType} onChange={(e) => setTargetType(e.target.value)} style={inputStyle}>
            <option value="all">Tüm hedefler</option>
            <option value="vehicle">Araç</option>
            <option value="site">Şantiye</option>
            <option value="facility">Tesis</option>
          </select>
          <select value={formName} onChange={(e) => setFormName(e.target.value)} style={inputStyle}>
            <option value="all">Tüm formlar</option>
            {formOptions.map((name) => <option key={name} value={name}>{name}</option>)}
          </select>
          <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} style={inputStyle} />
          <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} style={inputStyle} />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Plaka, eksiklik, açıklama ara..." style={{ ...inputStyle, minWidth: 240 }} />
        </div>
      </section>

      <section style={panelStyle}>
        <div style={{ overflowX: "auto" }}>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>İlk Tarih</th>
                <th style={thStyle}>Son Görülme</th>
                <th style={thStyle}>Tip</th>
                <th style={thStyle}>Hedef</th>
                <th style={thStyle}>Eksiklik</th>
                <th style={thStyle}>Açıklama</th>
                <th style={thStyle}>Tekrar</th>
                <th style={thStyle}>Form</th>
                <th style={thStyle}>Kullanıcı</th>
                <th style={thStyle}>Çözüm</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((item) => {
                const canResolve = canResolveItem({ item, user, canResolveAll, canResolveOwn });
                return (
                  <tr key={item.id}>
                    <td style={tdStyle}>{dateText(item.firstSeenAt)}</td>
                    <td style={tdStyle}>{dateText(item.lastSeenAt)}</td>
                    <td style={tdStyle}>{targetTypeText(item.targetType)}</td>
                    <td style={tdStyle}>{labelForTarget(item)}</td>
                    <td style={tdStyle}><b>{item.issue || "-"}</b></td>
                    <td style={tdStyle}>{item.lastNote || "-"}</td>
                    <td style={tdStyle}>{item.repeatCount || 1}</td>
                    <td style={tdStyle}>{item.formName || "-"}</td>
                    <td style={tdStyle}>{item.createdBy || latestUserForIssue(item) || "-"}</td>
                    <td style={tdStyle}>
                      {tab === "open" ? (
                        canResolve ? <button type="button" onClick={() => resolveIssue(item)} style={resolveBtn}>Çözüldü</button> : "-"
                      ) : (
                        <div>
                          <div>{dateText(item.resolvedAt)}</div>
                          <small>{item.resolvedBy || "-"}</small>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
              {!filtered.length && (
                <tr><td colSpan={10} style={{ ...tdStyle, color: "#64748b" }}>Kayıt bulunamadı.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

const pageStyle = { maxWidth: 1280, margin: "0 auto", display: "grid", gap: 12 };
const panelStyle = { background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8, padding: 14 };
const titleStyle = { margin: "0 0 12px", fontSize: 20 };
const toolbarStyle = { display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" };
const inputStyle = { padding: 8, border: "1px solid #cbd5e1", borderRadius: 6, minWidth: 160 };
const tabBtn = { background: "#f8fafc", color: "#0f172a", border: "1px solid #cbd5e1", borderRadius: 6, padding: "8px 12px", cursor: "pointer" };
const activeTabBtn = { ...tabBtn, background: "#2563eb", borderColor: "#2563eb", color: "#fff" };
const tableStyle = { width: "100%", borderCollapse: "collapse", fontSize: 13 };
const thStyle = { border: "1px solid #cbd5e1", background: "#f8fafc", padding: 8, textAlign: "left", whiteSpace: "nowrap" };
const tdStyle = { border: "1px solid #e2e8f0", padding: 8, verticalAlign: "top" };
const resolveBtn = { background: "#16a34a", color: "#fff", border: "none", borderRadius: 6, padding: "7px 10px", cursor: "pointer" };
